import { all, call, getContext, put, select } from 'redux-saga/effects';

import { selectors as configSelectors } from '@elab/core/store/config';
import { getUserId } from '@elab/core/utils/user/sagaHelpers';
import { isNotEmptyField, isValidField } from '@elab/core/utils/validations';
import { compareObjectsByProperties } from '@elab/core/utils/objectUtils';
import { selectors as globalSelectors } from '@elab/core/store/global';

import { AddressService } from './AddressService';

export function* resetAddressValidation(action, actions, selectors) {
    yield put(actions.hideAddressSuggestionsList());

    if (action.payload.skipValidation) {
        return;
    }

    const { nestedFormReduxPath } = yield collectSuggestionsData({
        action,
        actions,
        selectors,
    });

    yield put(actions.setAddressValidation(false, !!nestedFormReduxPath));
}

export function* cleanupAddressFields(action, actions, selectors) {
    const data = yield call(collectSuggestionsData, {
        action,
        actions,
        selectors,
    });

    if (data.fieldsToCleanUp) {
        yield call(setFieldValues, data, data.fieldsToCleanUp);
        yield put(actions.setAddressValidation(true, !!data.nestedFormReduxPath));
    }
}

export function* onChangeFormAddressField(action) {
    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');

    const data = yield call(collectSuggestionsData, {
        action,
        actions,
        selectors,
    });

    // Property 'skipValidation' defines if we have to prevent suggestions.
    if (!data.countrySpecificConfig || action.payload.skipValidation) {
        return;
    }

    const formFields = yield call(getSourceFieldsData, data);
    if (formFields.isValid) {
        let { sourceFields } = formFields;
        const { fieldName } = yield call(getNestedFormReduxPath, action.payload.name);
        if ((data.countrySpecificConfig.independentFields || []).includes(fieldName)) {
            sourceFields = [sourceFields.find(field => field.name === fieldName)];
        }
        yield call(getSuggestions, data, sourceFields);
    } else {
        yield put(actions.hideAddressSuggestionsList());

        const notSourceTargetFields = data.countrySpecificConfig.targetFields.filter(
            targetField => !(data.countrySpecificConfig.sourceFields || []).includes(targetField),
        );

        yield call(setFieldValues, data, notSourceTargetFields);
    }
}

export function* collectSuggestionsData({
    action,
    actions,
    selectors,
    nestedFormReduxPath: initialNestedFormReduxPath = '',
}) {
    let nestedFormReduxPath = initialNestedFormReduxPath;
    let fieldName;

    if (!nestedFormReduxPath && action) {
        const fieldPathChunks = getNestedFormReduxPath(action.payload.name);
        fieldName = fieldPathChunks.fieldName;
        nestedFormReduxPath = fieldPathChunks.nestedFormReduxPath;
    }

    const selectedCountry = yield select(
        selectors.getDataFieldValue,
        `${nestedFormReduxPath}country`,
    );

    const config = yield select(configSelectors.getAddressSuggestionConfig);
    const countrySpecificConfig = config[selectedCountry] || {};
    const commonConfig = config.common || {};

    let fieldsToCleanUp;
    if (fieldName === commonConfig.cleanupTrigger) {
        fieldsToCleanUp = commonConfig.fieldsToCleanUp;
    }
    if (fieldName === countrySpecificConfig.cleanupTrigger) {
        fieldsToCleanUp = countrySpecificConfig.fieldsToCleanUp;
    }

    const isAddressField = (countrySpecificConfig.sourceFields || []).includes(fieldName);

    return {
        fieldName,
        selectedCountry,
        commonConfig,
        countrySpecificConfig,
        actions,
        selectors,
        nestedFormReduxPath,
        fieldsToCleanUp,
        isAddressField,
    };
}

export function getNestedFormReduxPath(fieldPath) {
    const namePosition = fieldPath.lastIndexOf('.') + 1;
    const isNestedForm = namePosition > 1;

    if (!isNestedForm) {
        return { fieldName: fieldPath, nestedFormReduxPath: '' };
    }

    return {
        fieldName: fieldPath.slice(namePosition),
        nestedFormReduxPath: fieldPath.slice(0, namePosition),
    };
}

export function* showSuggestedAddresses(
    suggestedAddresses,
    { actions, selectors, nestedFormReduxPath, fieldName },
) {
    const postalCode = yield select(
        selectors.getDataFieldValue,
        `${nestedFormReduxPath}postalCode`,
    );
    yield put(
        actions.showAddressSuggestionsList({
            suggestions: suggestedAddresses,
            sourceValue: postalCode,
            fieldName: nestedFormReduxPath + fieldName,
        }),
    );
}

export function* suggestionsStrategy(data, suggestedAddresses) {
    const fieldPath = data.nestedFormReduxPath + data.fieldName;
    const isFocused = yield select(data.selectors.isDataFieldFocused, fieldPath);
    const { instantAutofill, instantAutofillOnOne } = data.countrySpecificConfig;
    const { addressSuggestionList } = data.commonConfig;

    if (!suggestedAddresses) {
        return;
    }

    if (
        instantAutofill ||
        (suggestedAddresses.length === 1 && (!addressSuggestionList || instantAutofillOnOne))
    ) {
        yield setFieldValues(data, data.countrySpecificConfig.targetFields, suggestedAddresses[0]);
        yield put(data.actions.setAddressValidation(true, !!data.nestedFormReduxPath));
        return;
    }

    if (suggestedAddresses.length > 0 && addressSuggestionList && isFocused) {
        yield showSuggestedAddresses(suggestedAddresses, data);
    }
}

export function* callSuggestionsApi({ selectedCountry }, sourceFields) {
    const userId = yield call(getUserId);

    const payload = sourceFields.reduce(
        (data, field) => {
            data[field.name] = field.value;
            return data;
        },
        { country: { isocode: selectedCountry } },
    );

    return yield call(AddressService.getSuggestionsForAddress, { userId }, payload);
}

export function* getSourceFieldsData({
    fieldName,
    countrySpecificConfig,
    selectors,
    nestedFormReduxPath,
}) {
    const sourceFields = yield all(
        (countrySpecificConfig.sourceFields || []).map(name =>
            select(selectors.getDataField, nestedFormReduxPath + name),
        ),
    );
    const sourceFieldsByNames = sourceFields.reduce((allFields, field) => {
        allFields[field.name] = field;
        return allFields;
    }, {});

    let isValid;
    const customChecker = (countrySpecificConfig.emptinessCheckers || {})[fieldName];
    if (customChecker && sourceFieldsByNames[fieldName]) {
        isValid = customChecker.test(sourceFieldsByNames[fieldName].value);
    } else {
        const skipValidation =
            countrySpecificConfig.skipValidation &&
            countrySpecificConfig.skipValidation.includes(fieldName);
        const validationMethod = skipValidation ? isNotEmptyField : isValidField;
        isValid = sourceFields.every(validationMethod);
    }

    return {
        sourceFields,
        isValid,
    };
}

export function* getSuggestions(data, sourceFields) {
    const response = yield callSuggestionsApi(data, sourceFields);

    if (!response.errors) {
        yield suggestionsStrategy(data, response.suggestedAddresses);
    }
}

export function* setFieldValues(
    { nestedFormReduxPath, actions },
    fieldNames,
    sourceValues,
    saveSuggestedValues = [],
) {
    const insertions = fieldNames.map(fieldName => {
        const fieldPath = nestedFormReduxPath + fieldName;
        const value = sourceValues ? sourceValues[fieldName] : '';
        return put(
            actions.setDataFieldValue(
                fieldPath,
                value,
                true,
                false,
                saveSuggestedValues.includes(fieldName),
            ),
        );
    });
    yield all(insertions);
}

export function* isConfirmationDialogEnabled() {
    const { enabledCountries } = yield select(configSelectors.getAddressVerificationDialogConfig);
    const siteCountry = yield select(globalSelectors.getCountry);

    return Array.isArray(enabledCountries) && enabledCountries.includes(siteCountry);
}

export const isAddressesEqual = (
    address1,
    address2,
    comparableProperties,
    comparablePropertiesForCountry,
) =>
    compareObjectsByProperties(address1, address2, comparableProperties) &&
    compareObjectsByProperties(address1.country, address2.country, comparablePropertiesForCountry);
